import React, { useEffect, useState } from 'react';
import { FaEye, FaEdit, FaBan, FaDownload, FaPlus, FaSort, FaSortUp, FaSortDown, FaCheck } from 'react-icons/fa';
import dayjs from 'dayjs';
import TaxFormModal from './TaxFormModal';
import TaxViewModal from './TaxViewModal';
import Sidebar from './Sidebar';
import { useNavigate } from 'react-router-dom';

const API_URL = '/api';

const TaxMaster = () => {
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedTax, setSelectedTax] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTaxes();
  }, []);

  // Add scroll listener for sticky header behavior
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchTaxes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/taxes`);
      const data = await res.json();
      setTaxes(data);
    } catch {
      setTaxes([]);
    }
    setLoading(false);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedTaxes = [...taxes].sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;
      
      // Handle numeric fields
      if (key === 'TaxCode') {
        const aNum = parseInt(a[key].split('-')[1]);
        const bNum = parseInt(b[key].split('-')[1]);
        return direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Handle string fields
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setTaxes(sortedTaxes);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ml-1" />;
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="ml-1" /> 
      : <FaSortDown className="ml-1" />;
  };

  const handleView = (tax) => {
    setSelectedTax(tax);
    setShowView(true);
  };

  const handleEdit = (tax) => {
    setSelectedTax(tax);
    setIsEdit(true);
    setShowForm(true);
  };

  const handleDeactivate = async (tax) => {
    if (!window.confirm('Are you sure you want to deactivate this tax?')) return;
    
    try {
      const response = await fetch(`${API_URL}/taxes/${tax.TaxCode}/deactivate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to deactivate tax');
      }
      
      fetchTaxes();
    } catch (error) {
      console.error('Error deactivating tax:', error);
      alert(error.message || 'An error occurred while deactivating the tax');
    }
  };

  const handleReactivate = async (tax) => {
    if (!window.confirm('Are you sure you want to reactivate this tax?')) return;
    
    try {
      const response = await fetch(`${API_URL}/taxes/${tax.TaxCode}/reactivate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reactivate tax');
      }
      
      fetchTaxes();
    } catch (error) {
      console.error('Error reactivating tax:', error);
      alert(error.message || 'An error occurred while reactivating the tax');
    }
  };

  const handleDownloadCSV = () => {
    const headers = [
      'TaxCode', 'TaxType', 'TaxName', 'Description', 'Rate', 'Country', 'IsDefault', 'Status'
    ];
    const rows = taxes.map(t => [
      t.TaxCode, t.TaxType, t.TaxName, t.Description, t.Rate, t.Country, 
      t.IsDefault ? 'Yes' : 'No', t.Status
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'taxes.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCreate = () => {
    setSelectedTax(null);
    setIsEdit(false);
    setShowForm(true);
  };

  const handleFormSubmit = async (formData) => {
    setFormLoading(true);
    try {
      if (isEdit && selectedTax) {
        const response = await fetch(`${API_URL}/taxes/${selectedTax.TaxCode}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update tax');
        }
      } else {
        const response = await fetch(`${API_URL}/taxes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create tax');
        }
      }
      setShowForm(false);
      fetchTaxes();
    } catch (error) {
      console.error('Error saving tax:', error);
      alert(error.message || 'An error occurred while saving the tax');
    } finally {
      setFormLoading(false);
    }
  };

  const getStatusColor = (status) => {
    // Handle boolean, string, and numeric status values
    const isActive = status === true || status === 'Active' || status === 1 || status === '1';
    return isActive ? 'bg-[#52ab98] text-white' : 'bg-gray-300 text-gray-500';
  };

  const getStatusText = (status) => {
    // Convert boolean, numeric or string status to text
    if (status === true || status === 1 || status === '1') return 'Active';
    if (status === false || status === 0 || status === '0') return 'Inactive';
    return status || 'Unknown';
  };

  const getDefaultText = (isDefault) => {
    // Convert boolean, numeric or string IsDefault to text
    if (isDefault === true || isDefault === 1 || isDefault === '1') return 'Yes';
    if (isDefault === false || isDefault === 0 || isDefault === '0') return 'No';
    return isDefault ? 'Yes' : 'No';
  };

  const getDefaultColor = (isDefault) => {
    // Handle boolean, string, and numeric IsDefault values
    const isDefaultValue = isDefault === true || isDefault === 1 || isDefault === '1';
    return isDefaultValue ? 'bg-[#52ab98] text-white' : 'bg-gray-300 text-gray-500';
  };

  const handleLogout = () => {
    navigate('/login');
  };

 return (
  <div className="min-h-screen flex bg-[#E5E1DA]">
    <Sidebar onLogout={handleLogout} />
    <main
      className="flex-1 p-2 md:p-8 overflow-hidden ml-0 md:ml-16 transition-all duration-300"
    >         <div className="flex flex-col h-full">
          <div className="flex flex-col sm:flex-row justify-center sm:justify-between items-center text-center sm:items-center mb-4 md:mb-6 gap-4">
            <h1 className="text-2xl md:text-3xl font-bold text-[#2b6777]">Tax Master</h1>
            <button
              className="flex items-center gap-2 bg-[#92C7CF] hover:bg-[#AAD7D9] text-white font-semibold px-4 py-2 rounded-lg shadow transition w-full sm:w-auto justify-center"
              onClick={handleCreate}
            >
              <FaPlus /> Create New Tax
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            <div className="h-full overflow-auto overflow-x-auto scroll-smooth">
              <table className="w-full bg-white rounded-2xl shadow-lg border-separate border-spacing-0 min-w-[800px]">
                <thead>
                  <tr>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] w-auto text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold rounded-tl-2xl cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('TaxCode')}
                    >
                      <div className="flex items-center justify-center">
                        Tax Code {getSortIcon('TaxCode')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('TaxType')}
                    >
                      <div className="flex items-center justify-center">
                        Tax Type {getSortIcon('TaxType')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('TaxName')}
                    >
                      <div className="flex items-center justify-center">
                        Tax Name {getSortIcon('TaxName')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('Rate')}
                    >
                      <div className="flex items-center justify-center">
                        Rate (%) {getSortIcon('Rate')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('Country')}
                    >
                      <div className="flex items-center justify-center">
                        Country {getSortIcon('Country')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('IsDefault')}
                    >
                      <div className="flex items-center justify-center">
                        Default {getSortIcon('IsDefault')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('Status')}
                    >
                      <div className="flex items-center justify-center">
                        Status {getSortIcon('Status')}
                      </div>
                    </th>
                    <th className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm font-bold rounded-tr-2xl transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="text-center py-10 text-[#AAD7D9] font-semibold">
                        Loading taxes...
                      </td>
                    </tr>
                  ) : taxes.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-10 text-[#AAD7D9] font-semibold">
                        No taxes found.
                      </td>
                    </tr>
                  ) : taxes.map((tax, idx) => (
                    <tr
                      key={tax.TaxCode}
                      className={`transition-all duration-300 group ${getStatusText(tax.Status) === 'Active' ? 'hover:bg-[#eaf6f6] hover:shadow-md' : 'opacity-60'} border-b last:border-b-0 transform hover:scale-[1.01]`}
                      style={{ boxShadow: idx === taxes.length - 1 ? '0 8px 24px 0 rgba(44,103,119,0.04)' : undefined }}
                    >
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm font-mono whitespace-nowrap flex justify-center items-center">
                        <p className='cursor-pointer transition-all duration-200 hover:scale-105' style={{ color: 'blue', borderBottom: '1px solid blue', width: 'fit-content' }} onClick={() => handleView(tax)}>{tax.TaxCode}</p>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm whitespace-nowrap font-semibold">{tax.TaxType}</td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm whitespace-nowrap">{tax.TaxName}</td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm whitespace-nowrap font-bold">{tax.Rate}%</td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm whitespace-nowrap">{tax.Country || 'N/A'}</td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold shadow-sm transition-all duration-200
                          ${getDefaultColor(tax.IsDefault)}`}>
                          {getDefaultText(tax.IsDefault)}
                        </span>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold shadow-sm transition-all duration-200
                          ${getStatusColor(tax.Status)}`}>
                          {getStatusText(tax.Status)}
                        </span>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4 flex gap-2 justify-center items-center">
                        <button title="View" className="p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110" onClick={() => handleView(tax)}>
                          <FaEye />
                        </button>
                        <button title="Edit" className="p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110" onClick={() => handleEdit(tax)}>
                          <FaEdit />
                        </button>
                        {getStatusText(tax.Status) === 'Active' ? (
                          <button
                            title="Deactivate"
                            className="p-2 rounded-full hover:bg-red-100 text-red-500 transition-all duration-200 hover:scale-110"
                            onClick={() => handleDeactivate(tax)}
                          >
                            <FaBan />
                          </button>
                        ) : (
                          <button
                            title="Reactivate"
                            className="p-2 rounded-full hover:bg-green-100 text-green-500 transition-all duration-200 hover:scale-110"
                            onClick={() => handleReactivate(tax)}
                          >
                            <FaCheck />
                          </button>
                        )}
                        <button title="Download CSV" className="p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110" onClick={handleDownloadCSV}>
                          <FaDownload />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <TaxFormModal
            open={showForm}
            onClose={() => setShowForm(false)}
            onSubmit={handleFormSubmit}
            initialData={selectedTax}
            isEdit={isEdit}
            loading={formLoading}
          />
          <TaxViewModal
            open={showView}
            onClose={() => setShowView(false)}
            tax={selectedTax}
          />
        </div>
      </main>
    </div>
  );
};

export default TaxMaster; 